import React from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import { useGetAllProjectsForMatrixQuery, useGetMyProjectsQuery } from "../../../store/appApi";
import { setActiveProject, selectActiveProjectId } from "../../../store/projectSlice";
import { selectUserRole } from "../../../store/authSlice";
import PageTitle from "../../layouts/PageTitle";

const ProjectCard = ({ project, isAssigned, isActive, onSelect }) => {
  const card = (
    <div
      className={`card project-card mb-0 h-100 ${isActive ? "border-primary" : ""} ${!isAssigned ? "project-card--disabled" : ""}`}
      style={{
        cursor: isAssigned ? "pointer" : "not-allowed",
        opacity: isAssigned ? 1 : 0.55,
        transition: "box-shadow 0.2s, transform 0.2s",
        border: isActive ? "2px solid #324336" : "1px solid rgba(0,0,0,.125)",
      }}
      onClick={isAssigned ? onSelect : undefined}
      onMouseEnter={(e) => {
        if (isAssigned) {
          e.currentTarget.style.boxShadow = "0 4px 20px rgba(102,88,221,0.25)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "";
        e.currentTarget.style.transform = "";
      }}
    >
      <div className="card-body d-flex flex-column gap-2 p-4">
        <div className="d-flex align-items-start justify-content-between">
          <div
            className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
            style={{
              width: 44,
              height: 44,
              background: isAssigned ? "#3243361a" : "#9999991a",
              color: isAssigned ? "#324336" : "#999",
              fontSize: 20,
            }}
          >
            <i className="fas fa-building" />
          </div>
          {isActive && (
            <span className="badge bg-primary">Active</span>
          )}
          {!isAssigned && (
            <span className="badge" style={{ background: "#ccc", color: "#555" }}>
              Not Assigned
            </span>
          )}
        </div>

        <h6 className="card-title mb-0 mt-2" style={{ fontWeight: 600, lineHeight: 1.3 }}>
          {project.projectName}
        </h6>
        {project.city && (
          <p className="text-muted mb-0" style={{ fontSize: 13 }}>
            <i className="ti-location-pin me-1" />
            {project.city}
          </p>
        )}
        {project.reraNumber && (
          <p className="text-muted mb-0" style={{ fontSize: 12 }}>
            RERA: {project.reraNumber}
          </p>
        )}
      </div>
    </div>
  );

  if (!isAssigned) {
    return (
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip>
            You are not assigned to this project. Contact your administrator for access.
          </Tooltip>
        }
      >
        <div className="h-100">{card}</div>
      </OverlayTrigger>
    );
  }

  return card;
};

const ProjectDashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const activeProjectId = useSelector(selectActiveProjectId);
  const userRole = useSelector(selectUserRole);
  const isSuperAdmin = userRole === "super-admin";

  const { data: allProjectsData, isLoading: allLoading } = useGetAllProjectsForMatrixQuery();
  const { data: myProjectsData, isLoading: myLoading } = useGetMyProjectsQuery();

  // Backend returns { data: [...], pagination: {...} } for getProjects
  const allProjects = allProjectsData?.data ?? allProjectsData?.projects ?? [];
  // Super-admins can access every project; staff only their assigned ones
  const myProjectIds = isSuperAdmin
    ? new Set(allProjects.map((p) => p._id?.toString() ?? p.id?.toString()))
    : new Set((myProjectsData?.projects ?? []).map((p) => p._id?.toString() ?? p.id?.toString()));

  const handleSelectProject = (project) => {
    const id = project._id?.toString() ?? project.id?.toString();
    dispatch(
      setActiveProject({
        id,
        name: project.projectName,
        city: project.city,
      }),
    );
    navigate(`/dashboard/project/${id}`);
  };

  const isLoading = allLoading || myLoading;

  return (
    <>
      <PageTitle activeMenu="Select Project" motherMenu="Dashboard" />

      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex align-items-center gap-3">
            <div>
              <h4 className="mb-1" style={{ fontWeight: 700 }}>Project Workspace</h4>
              <p className="text-muted mb-0">Select an assigned project to open its workspace.</p>
            </div>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : allProjects.length === 0 ? (
        <div className="alert alert-info">
          No projects have been created yet. Contact your administrator.
        </div>
      ) : (
        <>
          {myProjectIds.size > 0 && (
            <div className="row mb-4">
              <div className="col-12">
                <h6 className="text-uppercase text-muted fw-bold mb-3" style={{ letterSpacing: 1, fontSize: 11 }}>
                  Your Assigned Projects
                </h6>
                <div className="row g-3">
                  {allProjects
                    .filter((p) => myProjectIds.has(p._id?.toString() ?? p.id?.toString()))
                    .map((project) => {
                      const id = project._id?.toString() ?? project.id?.toString();
                      return (
                        <div key={id} className="col-sm-6 col-md-4 col-lg-3">
                          <ProjectCard
                            project={project}
                            isAssigned
                            isActive={activeProjectId === id}
                            onSelect={() => handleSelectProject(project)}
                          />
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          )}

          {allProjects.some((p) => !myProjectIds.has(p._id?.toString() ?? p.id?.toString())) && (
            <div className="row">
              <div className="col-12">
                <h6 className="text-uppercase text-muted fw-bold mb-3" style={{ letterSpacing: 1, fontSize: 11 }}>
                  Other Projects
                </h6>
                <div className="row g-3">
                  {allProjects
                    .filter((p) => !myProjectIds.has(p._id?.toString() ?? p.id?.toString()))
                    .map((project) => {
                      const id = project._id?.toString() ?? project.id?.toString();
                      return (
                        <div key={id} className="col-sm-6 col-md-4 col-lg-3">
                          <ProjectCard
                            project={project}
                            isAssigned={false}
                            isActive={false}
                            onSelect={null}
                          />
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default ProjectDashboard;
